import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { AuthSessionContext } from '@context/AuthContext'

import { ActionIcon } from '@ui/components/ActionIcon'
import { InfoIcon } from '@ui/icons/Info.icon'
import {
  SortHeaderAlignType,
  Table,
  TableBody,
  TableCell,
  TableHeadSort,
  TableHeader,
  TableNoResults,
  TableRow,
} from '@ui/v2/Table/Table'

import { PageQuery } from '@utils/ApiClient'
import { formatDate } from '@utils/date.utils'
import { getCentralRowIdConditionally } from '@utils/getItemId'

import { ClientCampaignRebateResultDto } from '@model/ClientCampaignRebateResultDto'
import {
  ClientRebateCampaignDetailDto,
  isCampaignCalculationAccount,
  isCampaignClientRebate,
} from '@model/ClientRebateCampaignDetailDto'
import { NameDto } from '@model/NameDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

interface CampaignRebateTableProps {
  sortOptions: (NameDto<string> & { align: SortHeaderAlignType })[]
  campaign: ClientRebateCampaignDetailDto

  data: ClientCampaignRebateResultDto[]

  pageQuery?: PageQuery
  setPageQuery(value: PageQuery): void

  setInfoModal(v: boolean): void

  isLoading?: boolean
  sticky?: 'left'
}

export const CampaignRebateTable: React.FC<CampaignRebateTableProps> = (props) => {
  const { sortOptions, data, pageQuery, setPageQuery, isLoading = false, sticky } = props

  const { t } = useTranslation()

  return (
    <Table
      isLoading={isLoading}
      sticky={sticky}
      noResults={!data.length && <TableNoResults title={t('No results found')} />}
    >
      <TableHeader>
        <TableRow>
          {sortOptions.map((x) => (
            <TableHeadSort
              align={x.align || 'center'}
              id={x.id}
              sort={pageQuery?.sort || undefined}
              sortOrder={pageQuery?.sortOrder}
              setSort={(sort, sortOrder) => {
                setPageQuery({
                  ...(pageQuery || {}),
                  sort,
                  sortOrder,
                })
              }}
            >
              {x.name}
            </TableHeadSort>
          ))}
        </TableRow>
      </TableHeader>

      <TableBody>
        {data.map((roundResult, index) => (
          <CampaignRebateTableRow
            key={index}
            {...props}
            item={roundResult}
            {...getCentralRowIdConditionally(index, data.length, 'campaign-total-rebate')}
          />
        ))}
      </TableBody>
    </Table>
  )
}

interface CampaignRebateTableRowProps
  extends Pick<CampaignRebateTableProps, 'campaign' | 'setInfoModal'> {
  item: ClientCampaignRebateResultDto
  id?: string
}

const CampaignRebateTableRow: React.FC<CampaignRebateTableRowProps> = (props) => {
  const { item, campaign, setInfoModal, id } = props

  const { t } = useTranslation()
  const { formatMoney } = useFormatNumber()
  const [auth] = useContext(AuthSessionContext)

  const dateFormat = auth?.dateFormatType?.name
  const currencyId = campaign?.tradingAccount?.currencyId ?? item.currency.id

  return (
    <TableRow id={id}>
      <TableCell>
        <span className='is-flex is-justify-content-flex-start'>
          {formatDate(item.round, { formatType: dateFormat })}
        </span>
      </TableCell>

      <TableCell align='center'>
        <span className='is-flex is-justify-content-center'>
          {item.lots} {t('Campaigns.Lots')}
        </span>
      </TableCell>

      {hasRebateValueColumn(campaign) && (
        <TableCell align='center'>
          <span className='is-flex is-justify-content-center'>{item.rebateValue || '0'}</span>
        </TableCell>
      )}

      <TableCell align='center'>
        <span className='is-flex is-justify-content-center'>
          {formatMoney(item.rebate, currencyId)}
          {item.rebate > 0 && item.rebate < 1 && (
            <span className='ml-2'>
              <ActionIcon icon={InfoIcon} size='x20' onClick={() => setInfoModal(true)} />
            </span>
          )}
        </span>
      </TableCell>
    </TableRow>
  )
}

const hasRebateValueColumn = (detail?: ClientRebateCampaignDetailDto) => {
  return isCampaignClientRebate(detail) && !isCampaignCalculationAccount(detail)
}
